import { DoorOpen, MapPin, Route, Shapes } from 'lucide-react'

import { isStageBody, useEditorStore } from '@/stores/editorStore'

import { conversionTargets, type ConvertDomain } from './convert'
import { useKindUi, type IconComponent } from './KindPicker'
import type { StageDomain } from './vocab'

/**
 * "Change to…" — the one place the row menu, the details panel and anything else that offers
 * a conversion get their words, icons and the action from, so they can never disagree.
 */

export const TARGET_ICON: Readonly<Record<ConvertDomain, IconComponent>> = {
  region_kind: Shapes as unknown as IconComponent,
  anchor_kind: MapPin as unknown as IconComponent,
  portal_kind: DoorOpen as unknown as IconComponent,
  path_kind: Route as unknown as IconComponent,
}

/** "A spot…" / "Spots…" */
export function targetLabel(to: ConvertDomain, many = false): string {
  switch (to) {
    case 'region_kind':
      return many ? 'Shapes…' : 'A shape…'
    case 'anchor_kind':
      return many ? 'Spots…' : 'A spot…'
    case 'portal_kind':
      return many ? 'Ways in and out…' : 'A way in or out…'
    case 'path_kind':
      return many ? 'Paths…' : 'A path…'
  }
}

/** What each conversion does, for a tooltip. */
export const TARGET_HINT: Readonly<Record<ConvertDomain, string>> = {
  region_kind: 'An outline on the picture (a spot becomes a small box standing where it is).',
  anchor_kind: 'A point characters use. A shape becomes a spot where it touches the ground; a way in or out becomes the spot it stood on.',
  portal_kind: 'A way in or out standing on this spot (the spot stays, as its place).',
  path_kind: 'A route characters follow.',
}

/** The lists some of `ids` can become, from the current document (none for layers and paths). */
export function useChangeTargets(ids: readonly string[]): ConvertDomain[] {
  const doc = useEditorStore((s) => s.doc)
  return conversionTargets(isStageBody(doc) ? doc : null, ids)
}

/** Ask which kind of `to` the features become (the searchable picker), then convert them. */
export function openChangeTo(to: StageDomain, ids: readonly string[]): void {
  useKindUi.getState().openPicker(to, { kind: 'convert', ids: [...ids] })
}
